import type { Service } from "@/types/api";
import type { ExplorerItem, ExplorerSection } from "./explorerData";
import { canStageAs, type StageTabId, type StudioView } from "./stagingModel";

// Which Explorer sections the open tab shows: only content with somewhere to
// land, mirroring explorerDragAllowed's row gate at the section level. The
// Tests tab takes suites, a stage tab its stageable providers (canStageAs)
// plus schemas. The Full pipeline view has no drop targets, but its tree
// stays complete (minus suites) — it's still the place to browse, preview
// and read the "in use" badges. State-based drag locks (a filled SOURCE
// slot, staged rows, a view-only studio) deliberately do NOT hide sections:
// what fits the tab stays visible, draggable or not.

export interface ExplorerTabTarget {
  view: StudioView;
  tab: StageTabId;
}

/** The item itself is a hit: its name, or the prefix its label shows
 *  ("JSON - name"), so filtering by encoding finds the schema leaves too. */
export function matchesSelf(item: ExplorerItem, filter: string): boolean {
  if (item.name.toLowerCase().includes(filter)) return true;
  return !!item.labelPrefix?.toLowerCase().includes(filter);
}

/** The item is a hit, or has one somewhere inside it. */
export function matchesFilter(item: ExplorerItem, filter: string): boolean {
  if (matchesSelf(item, filter)) return true;
  return (item.children ?? []).some((c) => matchesFilter(c, filter));
}

function providerOf(section: ExplorerSection): Service["provider"] | null {
  return section.id.startsWith("provider:")
    ? (section.id.slice("provider:".length) as Service["provider"])
    : null;
}

function sectionAllowedOnTab(
  section: ExplorerSection,
  target: ExplorerTabTarget,
): boolean {
  if (target.view === "full") return section.id !== "test-suites";
  if (target.tab === "tests") return section.id === "test-suites";
  if (section.id === "test-suites") return false;
  const provider = providerOf(section);
  if (!provider) return true;
  return canStageAs(target.tab, provider);
}

// The tab filter narrowed further by the search filter: only sections with a
// matching item survive a non-empty search.
export function visibleExplorerSections(
  sections: ExplorerSection[],
  target: ExplorerTabTarget,
  filter: string,
): ExplorerSection[] {
  const tabSections = sections.filter((s) => sectionAllowedOnTab(s, target));
  if (!filter) return tabSections;
  return tabSections.filter((s) =>
    s.items.some((i) => matchesFilter(i, filter)),
  );
}

const TAB_EMPTY_HINTS: Record<Exclude<StageTabId, "tests">, string> = {
  source: "No source services yet",
  transform: "No script services yet",
  sink: "No sink services yet",
};

// The empty-tree hint, once loading has settled: the generic "No services
// yet" when nothing exists at all, a tab-naming variant when services exist
// but none can stage on the open tab (e.g. Transform with no UDF service).
// The Tests tab never lists services, so it never hints; the search filter
// hiding everything isn't emptiness either — hints ignore it.
export function explorerEmptyHint(
  target: ExplorerTabTarget,
  sections: ExplorerSection[],
  state: { servicesLoading: boolean; hasServices: boolean },
): string | null {
  if (state.servicesLoading) return null;
  if (target.view === "full")
    return state.hasServices ? null : "No services yet";
  if (target.tab === "tests") return null;
  if (!state.hasServices) return "No services yet";
  const hasProviderSection = sections.some(
    (s) => providerOf(s) !== null && sectionAllowedOnTab(s, target),
  );
  return hasProviderSection ? null : TAB_EMPTY_HINTS[target.tab];
}
